import Button from 'components/button/Button';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import {
  BUTTON_AUTO_CLICK_TIME_DURATION,
  CONTINUE_TO_START_TIME_DURATION,
} from 'constants/photo.const';
import { isEqualVal } from 'helpers/string.helper';
import { useTimer } from 'hooks/useTimer';
import { useTranslation } from 'hooks/useTranslation';
import { map, size } from 'lodash';
import React from 'react';
import { useAppSelector } from 'store/store-hooks';
import { useShootingContext } from './ShootingContext';
import { useShootingActions } from './useShootingActions';

interface ShootingControlsProps {
  countDownSec: number;
  delayTimeCountDownSeconds: number;
  timeLimitSeconds: number;
  handleStartPhoto: () => void;
  handleContinueOrConfirmReTakePhoto: () => void;
}

export const ShootingControls: React.FC<ShootingControlsProps> = ({
  countDownSec,
  delayTimeCountDownSeconds,
  timeLimitSeconds,
  handleStartPhoto,
  handleContinueOrConfirmReTakePhoto,
}) => {
  const { t, T } = useTranslation();
  const {
    isRetakePhoto, // trong giai đoạn chụp lại
    isShootingOfRetakingPhoto, // trong giai đoạn chụp lại, và đang chụp ảnh
    wasRetakePhoto, // đã hoàn thành giai đoạn chụp lại
  } = useAppSelector((state) => state.selfeApp);
  const {
    currentCameraId,
    availableListCameras,
    isCameraSessionOpen,
    isCountDownMode,
    isFinishTakePhoto,
    isInDuringTakePhoto,
    isDelayShooting,
    isStartCountDownShooting,
    numberOfTakePicture,
    sizeOfNeedToTakePhoto,
  } = useShootingContext();
  const {
    handleRedirectPrev,
    handleForceNextDuringDelayShooting,
    handleSwitchCamera,
    handleRedirectNext,
  } = useShootingActions();

  // start button auto click
  const { second: btnStartSeconds } = useTimer({
    isEnabled: isCameraSessionOpen,
    totalMilliseconds: CONTINUE_TO_START_TIME_DURATION,
    timeUpFunc: () => handleStartPhoto?.(),
  });

  // continue button auto click
  const { second: btnContinueSeconds } = useTimer({
    isEnabled:
      isCameraSessionOpen &&
      !isShootingOfRetakingPhoto &&
      isRetakePhoto &&
      wasRetakePhoto,
    totalMilliseconds: BUTTON_AUTO_CLICK_TIME_DURATION,
    timeUpFunc: () => handleRedirectNext?.(),
  });

  return (
    <div className="action-wrapper">
      {(() => {
        // Khi chưa bắt đầu
        if (!isStartCountDownShooting) {
          return (
            <>
              <Button color="secondary" onClick={handleRedirectPrev}>
                {t('common:back')}
              </Button>
              <Button
                onClick={handleStartPhoto}
                disabled={!isCameraSessionOpen}
              >
                {t('common:start')} ({btnStartSeconds})
              </Button>
            </>
          );
        }

        // khi xem lại ảnh chụp
        if (!isShootingOfRetakingPhoto && isRetakePhoto) {
          return (
            <Button onClick={handleContinueOrConfirmReTakePhoto}>
              {t('common:continue')}{' '}
              {wasRetakePhoto && `(${btnContinueSeconds})`}
            </Button>
          );
        }

        const isInDelayTime =
          isCountDownMode || (isDelayShooting && delayTimeCountDownSeconds > 0);
        const timeInDelayTime = isDelayShooting
          ? delayTimeCountDownSeconds
          : Math.max(countDownSec, 0);

        return (
          <>
            {isInDelayTime && (
              <Button
                disabled={!isDelayShooting}
                onClick={handleForceNextDuringDelayShooting}
              >
                {t('common:continue')}
              </Button>
            )}
            <div className="shooting-infor">
              <div>
                <Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
                  {T('common:numberOfShootedPhoto')}
                </Typography>
                <Typography variant={TYPOGRAPHY_VARIANTS.H2}>
                  {`${sizeOfNeedToTakePhoto}/${numberOfTakePicture}`}
                </Typography>
              </div>
              <div>
                <Typography variant={TYPOGRAPHY_VARIANTS.SMALL}>
                  {isDelayShooting ? T('common:ready') : T('common:time')}
                </Typography>
                <Typography variant={TYPOGRAPHY_VARIANTS.H2}>
                  {isInDelayTime ? timeInDelayTime : timeLimitSeconds}S
                </Typography>
              </div>
            </div>
          </>
        );
      })()}

      {/* Nút chuyển camera */}
      {size(availableListCameras) > 1 && (
        <div className="switch-camera-wrapper">
          {map(availableListCameras, (item, ind) => (
            <Button
              key={item?.ID}
              variantFill={isEqualVal(item?.ID, currentCameraId)}
              onClick={handleSwitchCamera(item?.ID)}
              disabled={isInDuringTakePhoto || isFinishTakePhoto}
            >
              Camera {ind + 1}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};
